import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";

/**
 * În ce mod Stripe rulează serverul, de fapt.
 *
 * This replaced a Test/Live toggle that wrote a column nobody read, next to a
 * "test connection" button that checked the key started with "sk_" and then
 * said "Conexiune reușită!" without ever contacting Stripe. On the day
 * somebody switches to live keys, a panel that reports success for a test key
 * is worse than one that says nothing at all.
 *
 * The secret key lives in the environment, which is right — it has no business
 * in a database a hundred screens can read — so this panel cannot SET the
 * mode. It reports it: what the key says about itself, whether Stripe answered
 * when we used it, which webhook secrets are present, and what is still stored
 * from the other mode.
 */

type StripeStatus = {
  configured: boolean;
  key_fingerprint: string | null;
  mode: "live" | "test" | "unknown";
  reachable: boolean;
  account: { id: string; country: string | null; name: string | null; livemode: boolean } | null;
  error: string | null;
  webhooks: {
    platform_secret_set: boolean;
    platform_secret_fingerprint: string | null;
    connect_secret_set: boolean;
    endpoints: {
      id: string;
      url: string;
      status: string;
      api_version: string | null;
      enabled_events: string[];
      role: "platform" | "connect" | "unknown";
      missing_events: string[];
    }[];
    endpoints_error: string | null;
  };
  connect: { enabled: boolean; accounts: number | null; module_on: boolean; error: string | null };
  stale_ids: {
    plans_with_stripe_ids: number;
    clubs_with_connect_account: number;
    club_subscriptions_with_customer: number;
    athlete_subscriptions_with_customer: number;
    positively_test: number;
    undetermined: number;
  };
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-right text-slate-800">{children}</span>
  </div>
);

const Yes = () => <span className="font-bold text-emerald-600">da</span>;
const No = () => <span className="font-bold text-rose-600">nu</span>;

export default function StripeStatusPanel() {
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<StripeStatus>("/platform-settings/stripe-status");
      setStatus(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut verifica starea Stripe.");
      setStatus(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !status) {
    return <p className="mb-6 text-sm text-slate-400">Se verifică starea Stripe...</p>;
  }

  if (error || !status) {
    return (
      <div className="p-4 mb-6 border rounded-xl border-slate-200 bg-white">
        <p className="text-sm text-slate-500">{error}</p>
        <button onClick={load} className="mt-2 text-sm font-semibold text-indigo-600">
          Reîncearcă
        </button>
      </div>
    );
  }

  const live = status.mode === "live";
  const tone = !status.configured
    ? "border-slate-300 bg-slate-50"
    : !status.reachable
      ? "border-rose-300 bg-rose-50"
      : live
        ? "border-emerald-300 bg-emerald-50"
        : "border-amber-300 bg-amber-50";

  const headline = !status.configured
    ? "Stripe nu este configurat"
    : !status.reachable
      ? "Cheia nu funcționează"
      : live
        ? "MOD LIVE — se încasează bani reali"
        : status.mode === "test"
          ? "Mod test — nu se încasează bani reali"
          : "Cheie nerecunoscută";

  // Everything the go-live needs, in one list, with the stale rows last
  // because they are the part people forget.
  const stale = status.stale_ids;
  const staleTotal =
    stale.plans_with_stripe_ids +
    stale.clubs_with_connect_account +
    stale.club_subscriptions_with_customer +
    stale.athlete_subscriptions_with_customer;

  return (
    <div className={`p-4 mb-6 border-2 rounded-xl ${tone}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="font-bold text-slate-800">{headline}</h4>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto px-3 py-1.5 text-xs font-semibold bg-white border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? "Se verifică..." : "Verifică din nou"}
        </button>
      </div>

      <p className="mt-1 text-xs text-slate-600">
        Citit din cheia de pe server, nu dintr-o setare — și confirmat printr-un apel real la
        Stripe. Cheia se schimbă din variabilele Railway, nu de aici.
      </p>

      <div className="pt-3 mt-3 border-t divide-y border-slate-200/70 divide-slate-200/70">
        <Row label="Cheie secretă (STRIPE_SECRET_KEY)">
          {status.configured ? <Yes /> : <No />}
          {status.key_fingerprint && (
            // Last four characters only: enough to tell whether the value you
            // just pasted into Railway actually reached the server.
            <span className="ml-2 font-mono text-xs font-normal text-slate-400">
              {status.key_fingerprint}
            </span>
          )}
        </Row>
        <Row label="Mod">
          <span className={live ? "text-emerald-700" : status.mode === "test" ? "text-amber-700" : "text-rose-700"}>
            {status.mode === "live" ? "live" : status.mode === "test" ? "test" : "nerecunoscut"}
          </span>
        </Row>
        <Row label="Stripe răspunde">{status.reachable ? <Yes /> : <No />}</Row>
        {status.account && (
          <Row label="Cont Stripe">
            <span className="font-mono text-xs">{status.account.id}</span>
            {status.account.name && (
              <span className="block text-xs font-normal text-slate-500">{status.account.name}</span>
            )}
          </Row>
        )}
        {status.error && (
          <Row label="Eroare de la Stripe">
            <span className="text-xs font-normal text-rose-700">{status.error}</span>
          </Row>
        )}
        <Row label="Secret webhook platformă (STRIPE_WEBHOOK_SECRET)">
          {status.webhooks.platform_secret_set ? <Yes /> : <No />}
          {status.webhooks.platform_secret_fingerprint && (
            <span className="ml-2 font-mono text-xs font-normal text-slate-400">
              {status.webhooks.platform_secret_fingerprint}
            </span>
          )}
        </Row>
        <Row label="Secret webhook Connect (STRIPE_CONNECT_WEBHOOK_SECRET)">
          {status.webhooks.connect_secret_set ? (
            <Yes />
          ) : status.connect.module_on ? (
            <No />
          ) : (
            // Not a failure. Connect is only needed by the club-payments
            // module, and a platform that merely bills clubs for its own
            // subscription never touches it. A red "no" here sent somebody
            // looking for a problem that did not exist.
            <span className="font-semibold text-slate-400">nu e nevoie încă</span>
          )}
        </Row>
        <Row label="Stripe Connect">
          {status.connect.enabled ? (
            <span className="text-emerald-700">
              activat
              {status.connect.accounts !== null && (
                <span className="block text-xs font-normal text-slate-500">
                  {status.connect.accounts === 0
                    ? "niciun cont conectat"
                    : `${status.connect.accounts}+ conturi conectate`}
                </span>
              )}
            </span>
          ) : (
            <span className={status.connect.module_on ? "text-rose-600" : "text-slate-400"}>
              neactivat
            </span>
          )}
        </Row>
      </div>

      {/* What Stripe has on its side. The signing secret is write-only there,
          so no screen can prove the environment holds the right one — but a
          missing endpoint, a disabled one, one created in the other mode, or
          one subscribed to the wrong events are all visible, and all four look
          the same from the outside: the payment goes through and nothing
          happens in the application. */}
      {status.reachable && (
        <div className="p-3 mt-3 text-xs bg-white border rounded-lg border-slate-200">
          <p className="font-bold text-slate-700">Endpoint-uri de webhook în modul curent</p>
          {status.webhooks.endpoints_error ? (
            <p className="mt-1 text-slate-500">
              Nu am putut citi lista: {status.webhooks.endpoints_error}
            </p>
          ) : status.webhooks.endpoints.length === 0 ? (
            <p className="mt-1 leading-relaxed text-rose-700">
              Stripe nu are niciun endpoint în modul curent. Un webhook creat în modul test nu
              există în live — trebuie creat din nou cu dashboard-ul comutat pe LIVE.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {status.webhooks.endpoints.map((e) => (
                <li key={e.id} className="pt-2 border-t border-slate-100 first:border-0 first:pt-0">
                  <p className="font-mono break-all text-slate-700">{e.url}</p>
                  <p className="text-slate-500">
                    {e.role === "platform" ? "platformă" : e.role === "connect" ? "Connect" : "nerecunoscut"}
                    {" · "}
                    <span className={e.status === "enabled" ? "text-emerald-700" : "text-rose-700"}>
                      {e.status === "enabled" ? "activ" : e.status}
                    </span>
                    {" · "}
                    {e.enabled_events.length === 1 && e.enabled_events[0] === "*"
                      ? "toate evenimentele"
                      : `${e.enabled_events.length} evenimente`}
                  </p>
                  {e.missing_events.length > 0 && (
                    <p className="mt-1 leading-relaxed text-rose-700">
                      Lipsesc: <span className="font-mono">{e.missing_events.join(", ")}</span>
                      {e.missing_events.includes("checkout.session.completed") && (
                        <span className="block">
                          Fără <code>checkout.session.completed</code> plata reușește, dar aplicația
                          nu află niciodată: abonamentul rămâne „pending" la infinit.
                        </span>
                      )}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!status.webhooks.endpoints_error &&
            status.webhooks.endpoints.length > 0 &&
            !status.webhooks.endpoints.some((e) => e.role === "platform") && (
              <p className="mt-2 leading-relaxed text-rose-700">
                Niciun endpoint nu arată spre <code>/webhooks/stripe</code>. Plățile părinților nu
                se vor activa.
              </p>
            )}
        </div>
      )}

      {/* The one combination that is actually broken, said out loud. */}
      {status.connect.module_on && !status.connect.enabled && (
        <p className="p-2 mt-3 text-xs border rounded-lg text-rose-800 border-rose-200 bg-rose-50">
          Modulul de plăți ale cluburilor este PORNIT, dar contul nu e configurat ca platformă
          Connect. Cluburile nu se vor putea conecta și nicio plată de părinte nu va funcționa.
        </p>
      )}

      {!status.connect.module_on && !status.connect.enabled && (
        <p className="mt-3 text-xs text-slate-500">
          Connect e folosit doar de modulul de plăți ale cluburilor, care e oprit. Îl activezi din
          Stripe → Connect abia când pornești modulul; până atunci nu lipsește nimic.
        </p>
      )}

      {staleTotal > 0 && (
        <div className="p-3 mt-3 text-xs bg-white border rounded-lg border-slate-200">
          <p className="font-bold text-slate-700">
            ID-uri Stripe salvate în baza de date: {staleTotal}
            {stale.positively_test > 0 && (
              <span className="text-rose-700"> ({stale.positively_test} sigur din modul test)</span>
            )}
          </p>
          <p className="mt-1 leading-relaxed text-slate-500">
            Fiecare ID pe care îl dă Stripe aparține unui singur mod. Un <code>price_</code> creat
            în test nu există în live, iar cererea pentru el întoarce „No such price". La trecerea
            pe live, acestea trebuie golite și recreate.
          </p>
          {stale.undetermined > 0 && (
            <p className="mt-1 leading-relaxed text-amber-700">
              {stale.undetermined} dintre ele nu pot fi clasificate după ID:{" "}
              <code>cus_</code>, <code>sub_</code>, <code>acct_</code>, <code>price_</code> și{" "}
              <code>prod_</code> nu poartă niciun marcaj de mod. „Imposibil de spus" nu înseamnă
              „în regulă" — dacă au fost create înainte de trecerea pe live, sunt de test.
            </p>
          )}
          <ul className="mt-2 space-y-0.5 text-slate-600">
            <li>Planuri cu produs/preț Stripe: {stale.plans_with_stripe_ids}</li>
            <li>Cluburi cu cont Connect: {stale.clubs_with_connect_account}</li>
            <li>Abonamente de club cu client Stripe: {stale.club_subscriptions_with_customer}</li>
            <li>Abonamente de sportiv cu client Stripe: {stale.athlete_subscriptions_with_customer}</li>
          </ul>
        </div>
      )}
    </div>
  );
}
